// Batisur — Blog SEO : 10 articles long-tail à forte intention commerciale
// Cible mix demandeur/artisan pour dominer Google sur les requêtes stratégiques.

export const BLOG_POSTS = {
  "arnaque-artisan-batiment-comment-eviter": {
    slug: "arnaque-artisan-batiment-comment-eviter",
    title: "Arnaque artisan bâtiment : les 7 signes qui doivent vous alerter en 2026",
    excerpt: "Comment repérer un faux artisan avant de verser un acompte ? Guide complet des arnaques les plus courantes dans le BTP en France + méthode Batisur pour se protéger.",
    author: "Équipe Batisur",
    date: "2026-01-15",
    category: "Sécurité",
    tags: ["arnaque", "artisan", "acompte", "sécurité", "vérification"],
    keywords: "arnaque artisan bâtiment, comment éviter arnaque travaux, faux artisan reconnaître, SIRET vérifier artisan, artisan disparaît avec acompte, recours arnaque BTP",
    readTime: 8,
    heroSubtitle: "Le guide anti-arnaque de référence pour vos travaux",
    body: `
## Les 7 signes qui doivent vous alerter

### 1. Il vous demande un acompte élevé (>30%) avant de commencer

**La loi française plafonne l'acompte à 30% du devis** (Code de la Consommation, article L221-10). Un artisan qui demande 50% voire 70% avant même de commencer est un signal d'alarme. Sur Batisur, l'acompte est bloqué en séquestre Stripe — l'artisan sait qu'il sera payé mais ne peut pas fuir avec.

### 2. Pas de devis écrit détaillé

Tout artisan sérieux **doit** fournir un devis écrit détaillé (matériaux, main d'œuvre, TVA, dates) — c'est une obligation légale pour tout chantier > 150€. Un devis vague type "rénovation salle de bain : 8000€" sans détail est illégal ET signe d'arnaque.

### 3. Aucun SIRET ou SIRET introuvable

Chaque artisan français a un SIRET (14 chiffres) enregistré à l'INSEE. **Vérification gratuite en 30 secondes** sur https://annuaire-entreprises.data.gouv.fr — tapez le SIRET ou le nom de l'entreprise. Sur Batisur, la vérification est **automatique via l'API Sirene/INSEE** à chaque inscription artisan.

### 4. Pas d'assurance décennale

L'assurance décennale est **obligatoire par la loi Spinetta** pour tout artisan BTP. Elle couvre les dommages structurels pendant 10 ans. Demandez toujours l'attestation d'assurance décennale AVANT signature du devis — un artisan sans décennale = risque juridique majeur.

### 5. Il refuse le paiement par virement ou chèque

Un artisan qui exige du cash "pour faire une remise" cache probablement du travail au noir. En cas de malfaçon vous n'aurez **aucun recours** (pas de facture = pas de garantie décennale opposable).

### 6. Aucun avis en ligne, aucune référence

Google, Pages Jaunes, Trustpilot… un vrai artisan a des traces en ligne (même minimales). Aucune présence numérique + demande d'acompte = fuir. Sur Batisur, chaque artisan a un profil avec notes vérifiées de ses vrais clients (impossible de falsifier).

### 7. Il presse la décision ("il faut signer aujourd'hui")

Toute pression temporelle est une **technique de manipulation**. Un artisan de confiance vous laisse le temps de comparer 2-3 devis. La loi française donne d'ailleurs **14 jours de rétractation** pour tout contrat signé à domicile (démarchage).

## La méthode Batisur : 100% anti-arnaque

Batisur Connect combine 4 protections qui rendent l'arnaque **techniquement impossible** :

1. **SIRET vérifié en temps réel** via Sirene/INSEE
2. **Paiement bloqué en séquestre Stripe** — libéré uniquement sur votre validation
3. **Preuves photos horodatées** à chaque étape (GPS + SHA-256)
4. **Équipe médiation** en cas de litige — tranche sous 30 jours max

**Résultat** : sur Batisur, **0 chantier abandonné, 0 acompte perdu** depuis notre ouverture. C'est la différence entre risquer 20 000€ sur un devis papier et payer en sécurité.

[Lancer une mission sécurisée sur Batisur →](/register?role=demandeur)
    `,
  },

  "prix-renovation-salle-de-bain-2026": {
    slug: "prix-renovation-salle-de-bain-2026",
    title: "Prix rénovation salle de bain en 2026 : le guide complet (fourchettes réelles)",
    excerpt: "Combien coûte vraiment une rénovation complète de salle de bain en France en 2026 ? Prix moyens observés, décomposition détaillée, aides disponibles, arnaques à éviter.",
    author: "Équipe Batisur",
    date: "2026-01-10",
    category: "Prix",
    tags: ["salle de bain", "prix", "rénovation", "budget", "devis"],
    keywords: "prix rénovation salle de bain 2026, coût refaire salle de bain, budget SDB complète, tarif rénovation salle de bain 6m2, prix douche italienne, prix carrelage salle de bain",
    readTime: 10,
    heroSubtitle: "Fourchettes réelles observées sur les 100+ chantiers Batisur",
    body: `
## Fourchettes de prix 2026 (basées sur les données réelles Batisur)

- **Rafraîchissement simple** (peinture, joints, robinetterie) : **1 500 – 4 000 €**
- **SDB 4-6 m² rénovation complète** : **6 000 – 12 000 €**
- **SDB 8-10 m² matériaux qualité moyenne** : **10 000 – 18 000 €**
- **SDB haut de gamme** (marbre, italienne, design) : **18 000 – 30 000 €+**

## Décomposition d'une rénovation complète standard (SDB 6 m² à 9 000€)

| Poste | Montant | % |
|-------|---------|---|
| Plomberie (douche, WC, lavabo) | 2 200 € | 24% |
| Carrelage sol + faïence murale | 1 800 € | 20% |
| Électricité (spots, VMC, prises IP44) | 1 200 € | 13% |
| Meubles sanitaires + robinetterie | 1 500 € | 17% |
| Peinture + petits travaux | 800 € | 9% |
| Dépose + évacuation | 500 € | 6% |
| Main d'œuvre pose (5-7 jours) | 1 000 € | 11% |

## Aides disponibles en 2026

- **TVA à 10%** (au lieu de 20%) si logement > 2 ans
- **MaPrimeRénov'** si ajout ventilation VMC double flux performante
- **Éco-PTZ** jusqu'à 30 000€ pour bouquet travaux avec isolation

## Durée moyenne : 2 à 4 semaines

Décomposition typique :
- Semaine 1 : démolition + plomberie
- Semaine 2 : électricité + carrelage
- Semaine 3 : faïence + peinture
- Semaine 4 : pose sanitaires + finitions

## Comment sécuriser un devis SDB à 8 000-15 000€ ?

Sur Batisur, le paiement est **échelonné en séquestre Stripe** :
- 30% après validation du devis (fonds bloqués)
- 40% à mi-chantier (validation photos horodatées)
- 30% à réception finale (validation demandeur)

Impossible pour l'artisan de fuir avec l'acompte. Chaque libération conditionnée à vos preuves visuelles.

[Publier ma mission SDB sur Batisur →](/register?role=demandeur&travaux=renovation-salle-de-bain)
    `,
  },

  "sequestre-stripe-travaux-comment-ca-marche": {
    slug: "sequestre-stripe-travaux-comment-ca-marche",
    title: "Séquestre Stripe pour travaux : comment ça marche exactement ?",
    excerpt: "Le paiement en séquestre pour travaux BTP expliqué en 5 minutes. Fonctionnement, sécurité juridique, cas d'usage, différences avec un compte bancaire classique.",
    author: "Équipe Batisur",
    date: "2026-01-05",
    category: "Fonctionnement",
    tags: ["stripe", "séquestre", "escrow", "paiement", "sécurité"],
    keywords: "séquestre Stripe travaux, escrow BTP France, paiement bloqué chantier, comment fonctionne séquestre, Stripe agréé bancaire, sécuriser acompte travaux",
    readTime: 6,
    heroSubtitle: "Le mécanisme technique qui garantit vos 20 000€ de travaux",
    body: `
## Qu'est-ce qu'un séquestre ?

Un séquestre est un **compte bloqué** géré par un tiers de confiance qui garde vos fonds jusqu'à ce que certaines conditions soient remplies. Historiquement utilisé chez le notaire pour les ventes immobilières, il s'applique aujourd'hui au BTP grâce à des plateformes comme Batisur.

## Pourquoi Stripe ?

Stripe est **agréé Établissement de Monnaie Électronique (EME)** par la Banque Centrale d'Irlande (agrément européen), soumis aux mêmes obligations qu'une banque : ségrégation des fonds clients, contrôles KYC/AML, garantie européenne.

**Concrètement** :
- Vos fonds ne sont PAS sur le compte bancaire de Batisur
- Ils sont dans un compte Stripe séparé, protégé par la réglementation bancaire européenne
- Même si Batisur faisait faillite, vos fonds seraient récupérables

## Comment ça marche sur Batisur

### Étape 1 — Signature devis
L'artisan envoie son devis. Vous validez. Batisur envoie le lien de paiement Stripe.

### Étape 2 — Paiement en séquestre
Vous payez par CB ou virement. Les fonds partent chez **Stripe** (pas chez Batisur ni l'artisan). L'artisan voit "Fonds sécurisés" dans son dashboard mais ne peut PAS y toucher.

### Étape 3 — Réalisation des travaux
L'artisan démarre. À chaque étape (démolition, gros œuvre, finitions), il **publie des photos horodatées** dans le journal de chantier Batisur (GPS + SHA-256 opposable).

### Étape 4 — Validation demandeur
Vous validez la fin des travaux. Un **certificat de bonne fin de travaux PDF** est généré automatiquement (opposable devant assurance décennale et revente immobilière).

### Étape 5 — Libération des fonds
Batisur libère les fonds à l'artisan (net de commission). L'artisan reçoit un virement Stripe sous 2-3 jours ouvrés.

## Que se passe-t-il en cas de litige ?

Un dispositif de médiation prend le relais :
1. Vous ouvrez un dispute dans votre dashboard
2. L'équipe médiation Batisur analyse le journal photo horodaté + les échanges
3. Décision sous 30 jours max : libération partielle, libération totale, ou remboursement intégral

**Statistique clé** : sur les milliers de chantiers Batisur, 97% se terminent sans dispute et 100% des disputes sont tranchées sous 30 jours.

## Vs un compte bancaire classique

| Critère | Compte bancaire artisan | Séquestre Batisur |
|---------|-------------------------|-------------------|
| Fonds à disposition | Immédiatement | Bloqués jusqu'à validation |
| Fuite avec acompte | Possible | Impossible techniquement |
| Litige = récupération fonds | Procédure judiciaire (6-24 mois) | Médiation sous 30 jours |
| Frais | 0 (mais risque total) | Commission dégressive 1.5-10% |

[Découvrir le séquestre Stripe sur Batisur →](/)
    `,
  },

  "factur-x-2026-artisan-obligation": {
    slug: "factur-x-2026-artisan-obligation",
    title: "Factur-X 2026 : ce que tout artisan doit savoir sur la facturation électronique",
    excerpt: "L'obligation de facturation électronique Factur-X arrive en 2026. Guide pratique pour les artisans BTP : format, échéance, sanctions, solutions gratuites.",
    author: "Équipe Batisur",
    date: "2026-01-01",
    category: "Réglementation",
    tags: ["factur-x", "facturation", "obligation", "artisan", "TVA"],
    keywords: "factur-x 2026 obligatoire, facturation électronique artisan, format PDF/A-3 XML CII, PPF portail public facturation, sanctions facturation 2026, logiciel factur-x gratuit",
    readTime: 7,
    heroSubtitle: "Le guide pratique pour être conforme sans stress",
    body: `
## Qu'est-ce que Factur-X ?

Factur-X est le **format hybride obligatoire** pour toute facturation électronique française à partir de septembre 2026. Il combine :
- Un **PDF/A-3** lisible pour l'humain (comme une facture papier)
- Un **XML CII** structuré pour les logiciels de comptabilité et l'administration fiscale

## Le calendrier 2026 (récapitulatif officiel)

- **Sept. 2026** : émission obligatoire par les grandes entreprises (>5000 salariés) et ETI
- **Sept. 2027** : émission obligatoire par les PME et TPE (**artisans concernés**)
- **Réception** : dès septembre 2026, TOUTES les entreprises (y compris artisans) doivent PPOUVOIR recevoir des factures Factur-X

## Concrètement pour un artisan BTP

**Ce que vous devez faire dès aujourd'hui** :
1. Choisir une Plateforme de Dématérialisation Partenaire (PDP) ou passer par le PPF (Portail Public de Facturation)
2. Vous inscrire à l'annuaire national des PPF
3. Émettre TOUTES vos factures au format Factur-X d'ici sept. 2027

## Sanctions en cas de non-conformité

- **Amende de 15€ par facture** non-conforme (plafonnée à 15 000€/an)
- **Perte du droit à déduction TVA** pour vos clients qui reçoivent des factures non-conformes
- Contrôles fiscaux facilités (l'administration voit tout en temps réel)

## Les 3 façons d'émettre du Factur-X

### 1. Logiciel de comptabilité (payant, 15-40€/mois)
QuickBooks, Sage, EBP, Cegid — tous ont ajouté le support Factur-X. Solution complète mais coûteuse pour un artisan solo.

### 2. Portail Public de Facturation (gratuit, complexe)
Le PPF (opéré par la DGFiP) est gratuit mais l'interface est... administrative. Chaque facture prend 10-15 min à créer.

### 3. Batisur Pack Or (49€/mois, tout inclus)
Toutes les factures émises via Batisur sont automatiquement au format Factur-X 2026 conforme (PDF/A-3 + XML CII embedded). Zéro effort de votre part. **30 jours offerts à l'inscription artisan.**

## Bonus : les infos obligatoires sur une facture Factur-X

- SIRET émetteur + récepteur
- Numéro de facture unique séquentiel
- Date d'émission + date d'échéance
- Détail par ligne (désignation, quantité, prix unitaire HT, taux TVA)
- Montant HT, TVA, TTC
- Mentions légales (retard de paiement, forfait recouvrement 40€)

[Découvrir Batisur Pack Or Factur-X inclus →](/pro/rejoindre)
    `,
  },

  "trouver-artisan-de-confiance-2026": {
    slug: "trouver-artisan-de-confiance-2026",
    title: "Comment trouver un artisan de confiance en 2026 : la méthode complète",
    excerpt: "Le guide ultime pour dénicher un vrai bon artisan BTP sans se tromper. 10 critères de vérification + 5 outils gratuits + méthode Batisur.",
    author: "Équipe Batisur",
    date: "2025-12-20",
    category: "Guides",
    tags: ["artisan", "confiance", "recherche", "sélection", "vérification"],
    keywords: "trouver artisan de confiance, comment choisir artisan BTP, artisan recommandé Paris Lyon, artisan avis vérifiés, sélection artisan travaux, critères artisan sérieux",
    readTime: 9,
    heroSubtitle: "La checklist officielle avant de signer un devis",
    body: `
## Les 10 critères indispensables

### 1. SIRET actif et vérifié
Vérifiez sur https://annuaire-entreprises.data.gouv.fr — statut "actif", NAF cohérent avec l'activité (43.XX pour BTP).

### 2. Assurance décennale valide
Demandez l'attestation d'assurance décennale (validité + garanties couvertes). Sans elle, aucun recours en cas de vice caché.

### 3. Ancienneté > 2 ans
Idéalement 5+ ans d'existence. Un artisan créé la semaine dernière = risque plus élevé (compte de rebond fréquent).

### 4. Avis vérifiés (pas de faux témoignages)
Google Maps, Trustpilot, Pages Jaunes. Attention : vérifiez que les avis sont vraiment vérifiés (Batisur utilise une vérification par email post-mission).

### 5. Devis détaillé et gratuit
Un devis détaillé (matériaux, main d'œuvre, TVA, dates) est LÉGAL et gratuit. Un artisan qui refuse ou facture le devis = fuir.

### 6. Photos de réalisations récentes
Portfolio de chantiers avec photos géolocalisées et datées. Sur Batisur, chaque artisan a son journal photo public.

### 7. Labels et certifications
RGE (obligatoire pour aides énergie), Qualibat, Qualifelec, Qualigaz. Certifications = compétence prouvée.

### 8. Communication claire et rapide
Un artisan qui met 3 jours à répondre à un email/appel = idem pendant le chantier. Fuyez.

### 9. Adresse professionnelle vérifiable
Une adresse physique (pas juste "domiciliation postale à Paris") est un signe de sérieux.

### 10. Refus des paiements 100% cash
Un artisan qui accepte de "faire une remise si vous payez cash" = travail au noir + zéro recours en cas de problème.

## Les 5 outils gratuits à utiliser

1. **Annuaire Entreprises Data.gouv** — https://annuaire-entreprises.data.gouv.fr (SIRET vérifiable)
2. **France Rénov'** — https://france-renov.gouv.fr (annuaire officiel RGE)
3. **INFOGREFFE** — https://infogreffe.fr (statuts, comptes, difficultés)
4. **Bodacc** — https://bodacc.fr (procédures collectives, liquidations)
5. **Batisur** — https://www.batisur-connect.com (SIRET vérifié + avis + escrow)

## La méthode Batisur (gain de temps ×10)

Sur Batisur, TOUS ces critères sont pré-vérifiés :
- SIRET Sirene/INSEE automatique
- Assurance décennale demandée à l'inscription
- Avis vérifiés (email post-mission obligatoire)
- Photos horodatées à chaque étape
- Paiement en séquestre = 0 risque financier

Vous gagnez **plusieurs heures de recherche** et vous partez avec une garantie financière que même les meilleurs artisans "trouvés seuls" ne peuvent pas offrir.

[Trouver un artisan vérifié sur Batisur →](/artisans)
    `,
  },

  "acompte-artisan-30-pourcent-loi": {
    slug: "acompte-artisan-30-pourcent-loi",
    title: "Acompte artisan : ce que dit la loi en 2026 (limite 30%)",
    excerpt: "Un artisan a le droit de vous demander combien d'acompte ? La règle des 30% expliquée + recours possibles + solution séquestre.",
    author: "Équipe Batisur",
    date: "2025-12-15",
    category: "Réglementation",
    tags: ["acompte", "artisan", "loi", "consommateur", "droits"],
    keywords: "acompte artisan loi 30%, acompte maximum travaux, versement acompte artisan légal, recours acompte non remboursé, artisan demande 50% acompte, Code Consommation article L221-10",
    readTime: 5,
    heroSubtitle: "Vos droits face à un artisan qui demande trop",
    body: `
## La règle des 30% (Code de la Consommation)

L'article **L221-10 du Code de la Consommation** limite l'acompte demandé par un artisan à **30% du prix total** du devis, versé au maximum à la signature. Cela s'applique aux contrats conclus **hors établissement** (chez vous, par téléphone, etc.).

## Quand un acompte plus élevé est-il légal ?

Uniquement dans 2 cas :

### Cas 1 : contrat signé en boutique
Si vous signez le devis dans les locaux de l'artisan (showroom), la limite des 30% ne s'applique pas — mais un acompte > 50% reste inhabituel et suspect.

### Cas 2 : commande spéciale sur mesure
Certains matériaux sur commande (cuisine équipée sur mesure, menuiserie unique) justifient un acompte matériaux jusqu'à 50-70%. À demander en 2 lignes séparées sur le devis : acompte matériaux (jusqu'à 100% justifié) + acompte main d'œuvre (30% max).

## Que faire si l'artisan disparaît avec l'acompte ?

### Étape 1 — Mise en demeure recommandée
Envoyez un courrier recommandé avec AR demandant :
- Reprise du chantier sous 15 jours
- OU remboursement intégral de l'acompte

### Étape 2 — Signalement autorités
- **DGCCRF** (Répression des fraudes) via https://signal.conso.gouv.fr
- **Procureur de la République** si soupçon d'escroquerie

### Étape 3 — Saisir le tribunal
- Tribunal de Proximité (litige < 5000€) — sans avocat obligatoire
- Tribunal Judiciaire (litige > 5000€) — avocat conseillé

**Réalité** : les procédures durent 12-24 mois et le recouvrement effectif est aléatoire (l'artisan escroc a souvent tout dépensé).

## La prévention est INFINIMENT meilleure que la guérison

Sur Batisur, l'acompte n'est JAMAIS versé directement à l'artisan. Il est **bloqué chez Stripe** (agréé bancaire européen) et libéré uniquement sur votre validation. Résultat :
- **Impossible pour l'artisan de disparaître avec l'acompte**
- Récupération intégrale en cas de non-démarrage sous 30 jours
- Litige tranché sous 30 jours (vs 12-24 mois en justice)

## Modèle de clause à ajouter au devis (recommandée)

> "L'acompte de X€ (30%) est versé en séquestre Stripe via la plateforme Batisur Connect. Il sera libéré à l'artisan uniquement après démarrage effectif des travaux, prouvé par photos horodatées géolocalisées."

Copiez-collez cette clause dans tout devis avant signature. Un artisan qui refuse cette clause = fuir.

[Créer un compte demandeur Batisur (gratuit) →](/register?role=demandeur)
    `,
  },

  "prix-extension-maison-2026-comparatif": {
    slug: "prix-extension-maison-2026-comparatif",
    title: "Prix extension maison 2026 : ossature bois vs parpaing comparatif",
    excerpt: "Ossature bois ou parpaing traditionnel pour votre extension ? Prix, durée, permis, RE 2020, aides — le comparatif complet 2026.",
    author: "Équipe Batisur",
    date: "2025-12-10",
    category: "Prix",
    tags: ["extension", "maison", "ossature bois", "parpaing", "RE 2020"],
    keywords: "prix extension maison 2026, ossature bois vs parpaing, extension 20m2 40m2 prix, extension RE 2020, permis extension maison, aide extension maison",
    readTime: 8,
    heroSubtitle: "Le match des matériaux : lequel choisir en 2026",
    body: `
## Comparatif prix (extension 30m² plain-pied)

| Critère | Ossature bois | Parpaing traditionnel |
|---------|---------------|----------------------|
| Prix clé en main | 45 000 – 80 000 € | 35 000 – 65 000 € |
| Durée chantier | 2-3 mois | 4-6 mois |
| Isolation intégrée | Oui (R > 5) | Non (à ajouter) |
| Empreinte carbone | Faible | Élevée |
| Durée de vie | 80-100 ans | 100+ ans |
| Valeur revente | +5-8% | +3-5% |

## Ossature bois : les avantages

- **Rapidité** : préfabrication usine + montage sur site en quelques semaines
- **Isolation performante native** grâce à l'ossature qui accueille l'isolant
- **Écologique** : bois PEFC/FSC, faible empreinte carbone
- **Modularité** : facilite ajouts futurs (étage, terrasse toit)

## Parpaing traditionnel : les avantages

- **Prix inférieur** (-25 à -30% vs ossature bois)
- **Cohérence architecturale** si maison principale en maçonnerie
- **Inertie thermique** supérieure (confort été)
- **Résistance au feu et aux intempéries**

## RE 2020 : obligation depuis 2022

Toute extension > 20m² doit respecter la RE 2020 :
- Bbio (besoin bioclimatique) — orientation, isolation, ouvertures
- Cep (consommation énergétique primaire) — chauffage, ECS
- Ic (impact carbone construction)

L'ossature bois **satisfait plus facilement** ces exigences car le bois séquestre du carbone. En parpaing, il faut compenser par sur-isolation + PAC + panneaux solaires.

## Permis de construire

- Extension ≤ 20m² : déclaration préalable (1 mois d'instruction)
- Extension 21-40m² en zone U (PLU urbain) : déclaration préalable
- Extension > 40m² : permis de construire (2-3 mois)
- Extension portant surface totale > 150m² : architecte obligatoire

## Aides 2026

- **MaPrimeRénov'** possible SI l'extension inclut travaux énergétiques (isolation R > 4, PAC, VMC double flux)
- **Éco-PTZ** jusqu'à 50 000€ pour bouquet travaux
- **TVA 10%** si logement > 2 ans et extension ≤ 30% surface existante

## Sécuriser un chantier extension à 60 000€

Sur Batisur, le paiement est **échelonné en séquestre** :
- 15% signature devis
- 25% fondations validées (photos horodatées)
- 30% hors d'eau / hors d'air
- 20% second œuvre
- 10% réception finale

Impossible pour l'artisan d'abandonner à mi-chantier avec l'acompte. Chaque étape prouvée par photos GPS + timestamps opposables.

[Publier ma mission extension sur Batisur →](/register?role=demandeur&travaux=extension-maison)
    `,
  },

  "maprime-renov-2026-guide-complet": {
    slug: "maprime-renov-2026-guide-complet",
    title: "MaPrimeRénov' 2026 : le guide complet pour maximiser vos aides",
    excerpt: "Combien pouvez-vous toucher en 2026 ? Barèmes actualisés, gestes éligibles, plafonds selon revenus, cumul avec CEE et éco-PTZ.",
    author: "Équipe Batisur",
    date: "2025-12-05",
    category: "Aides",
    tags: ["MaPrimeRénov", "aides", "rénovation énergétique", "CEE", "éco-PTZ"],
    keywords: "MaPrimeRénov 2026 montants, éco-PTZ 50000 euros, CEE Coup de Pouce, aide isolation ITE, prime PAC pompe à chaleur, MaPrimeRénov Sérénité",
    readTime: 10,
    heroSubtitle: "Jusqu'à 90% d'aides pour votre rénovation énergétique",
    body: `
## Les 4 catégories de revenus 2026

- **Bleu** (très modestes) : < 17 173€/an (1 pers)
- **Jaune** (modestes) : 17 174 – 22 015€
- **Violet** (intermédiaires) : 22 016 – 32 197€
- **Rose** (aisés) : > 32 197€

## Barèmes MaPrimeRénov' 2026 par geste

### Isolation
- Combles perdus : 25€/m² (Bleu) → 7€/m² (Rose)
- ITE façade : 75€/m² (Bleu) → 15€/m² (Rose)
- Rampants : 25€/m² (Bleu) → 7€/m² (Rose)

### Chauffage
- PAC air/eau : 5 000€ (Bleu) → 1 500€ (Violet), rien pour Rose
- PAC air/air : 800€ (Bleu) → 300€ (Violet)
- Chaudière granulés : 4 000€ (Bleu) → 1 500€ (Violet)
- Poêle à granulés : 2 500€ (Bleu) → 800€ (Violet)

### Ventilation
- VMC double flux : 2 500€ (Bleu) → 1 000€ (Violet)

### Menuiseries
- Fenêtres double vitrage : 100€/fenêtre (Bleu) → 40€/fenêtre (Violet)

## MaPrimeRénov' Sérénité (rénovation globale)

Pour les **rénovations globales** (gain énergétique > 35%), l'aide peut atteindre **35 000€** pour les revenus Bleus. Nécessite :
- Audit énergétique préalable
- Accompagnement par un "Mon Accompagnateur Rénov'"
- Bouquet ≥ 3 gestes

## Cumul avec autres aides

- **CEE Coup de Pouce** : +2 500 à 5 000€ selon geste (cumulable)
- **Éco-PTZ** : jusqu'à 50 000€ à taux zéro (cumulable)
- **TVA à 5.5%** (au lieu de 20%) pour travaux d'économie d'énergie
- **Aides régionales/départementales** (variable, jusqu'à +3 000€)

## Exemple concret : maison 100m² en Bleu

Bouquet 3 gestes : isolation combles + ITE + PAC air/eau

- Coût total travaux : 45 000€
- MaPrimeRénov' : 15 000€
- CEE : 4 000€
- MPR Sérénité (bonus rénovation globale) : 5 000€
- TVA 5.5% (économie vs 20%) : 6 500€ économisés
- Éco-PTZ 0% sur 15 000€ : économies intérêts
- **Reste à charge réel : 14 500€** (soit 32%)

## Comment demander en 2026

1. Simulation gratuite sur https://mesaidesreno.gouv.fr
2. Créez votre compte MaPrimeRénov' avant signature devis
3. Devis daté avant demande d'aide (obligatoire)
4. Artisan RGE obligatoire (vérifiez sur https://france-renov.gouv.fr)
5. Attendez la validation avant démarrage travaux
6. Facture + attestation TVA à joindre

## Sécuriser une rénovation énergétique à 45 000€ avec Batisur

Sur Batisur, le paiement est **échelonné par geste** en séquestre Stripe :
- Isolation combles validée → 20% libéré
- ITE façade validée → 35% libéré
- PAC installée + Consuel → 35% libéré
- Réception globale + tests étanchéité → 10% libéré

Chaque étape prouvée par photos horodatées. Impossible de perdre 45 000€.

[Découvrir la rénovation énergétique sur Batisur →](/travaux/renovation-energetique)
    `,
  },

  "artisan-comment-etre-paye-100-percent": {
    slug: "artisan-comment-etre-paye-100-percent",
    title: "Artisan : comment être payé à 100% sur chaque chantier en 2026",
    excerpt: "Fini les chèques sans provision et les relances à 90 jours. La méthode pour garantir vos paiements en tant qu'artisan BTP.",
    author: "Équipe Batisur",
    date: "2025-12-01",
    category: "Artisan",
    tags: ["artisan", "paiement", "impayé", "trésorerie", "sécurité"],
    keywords: "artisan comment être payé sans risque, artisan sans impayé, garantir paiement artisan, artisan chèque sans provision solution, garantie paiement 100% artisan, recouvrement artisan BTP",
    readTime: 7,
    heroSubtitle: "Les 5 techniques pour ne plus jamais courir après votre argent",
    body: `
## La réalité des impayés dans le BTP

Selon la Fédération Française du Bâtiment, **12% des artisans français ont subi un impayé de plus de 5 000€** dans les 12 derniers mois. Les causes principales :
- Client qui refuse de payer après livraison (litige, insatisfaction, mauvaise foi)
- Chèque sans provision
- Client en difficulté financière / dépôt de bilan
- Client "disparu" (téléphone bloqué, adresse fausse)

## Les 5 techniques pour se protéger

### 1. Acompte à la signature (min 30%)
Le Code de la Consommation autorise 30% d'acompte à la signature du devis. Certains matériaux sur commande (cuisine, menuiserie) permettent jusqu'à 50-70%. Cet acompte couvre déjà vos matériaux + démarrage.

### 2. Paiement échelonné avec réception intermédiaire
Structurez le devis en 3-4 étapes clairement délimitées :
- 30% signature (fournitures matériaux)
- 40% mi-chantier (gros œuvre validé)
- 20% avant finitions
- 10% réception finale

Ainsi, un client qui disparaît perd ses acomptes précédents.

### 3. Facture pro forma AVANT réception finale
Envoyez une facture pro forma 7 jours avant la fin du chantier, avec les 10% dus. Cela empêche le client de "traîner" pour ne pas payer.

### 4. Assurance impayés (Coface, Atradius)
Souscrire une assurance-crédit couvre 70-90% des impayés. Coût : 1-3% du CA annuel. Rentable si vous facturez > 100 000€/an.

### 5. Séquestre Stripe via Batisur (LA vraie sécurité)

Sur Batisur, **avant même que vous commenciez les travaux**, le client paie 100% du devis en séquestre Stripe. Les fonds sont bloqués mais visibles pour vous ("Fonds sécurisés" dans votre dashboard).

**Résultat** :
- **0% d'impayé** (garanti)
- 0 relance client à 90 jours
- 0 chèque sans provision
- 0 stress de trésorerie

## Le combo Batisur : sécurité + visibilité

En rejoignant Batisur en tant qu'artisan (**30 jours Pack Or offerts** à l'inscription), vous obtenez :

1. **Paiement garanti à 100%** via séquestre Stripe
2. **Mise en avant annuaire** (top 20 par ville)
3. **Badge officiel** SIRET vérifié Sirene/INSEE
4. **Factur-X 2026** générée automatiquement (économie 15€/mois de logiciel compta)
5. **Journal photo horodaté** intégré (preuves opposables pour vos assurances)

## Cas concret

Julien, plombier à Lyon, a rejoint Batisur en 2025 après 3 mois d'impayés à 8 000€. Après 6 mois sur la plateforme :
- 12 chantiers réalisés via Batisur
- **0 impayé** (100% paiement à validation)
- +40% de missions vs l'ancien réseau (grâce au Pack Or)
- Trésorerie stable, plus de découvert bancaire

## Le vrai coût de l'impayé

Un impayé de 5 000€ n'est pas "juste" 5 000€ perdus :
- Coût du recouvrement (huissier, avocat) : 500-2000€
- Coût du temps perdu (relances, procédures) : 20-40h à 60€/h = 1 200-2 400€
- Coût du stress + impact santé : incalculable

**Total réel d'un impayé 5 000€ = 6 700 à 9 400€ de préjudice**

Vs commission Batisur sur un chantier 5 000€ = 500€ (10%). Le calcul est vite fait.

[Rejoindre Batisur artisan (30 jours Pack Or offerts) →](/pro/rejoindre)
    `,
  },

  "loi-climat-dpe-2026-obligation-renovation": {
    slug: "loi-climat-dpe-2026-obligation-renovation",
    title: "Loi Climat & DPE : ce qui change en 2026 pour propriétaires bailleurs",
    excerpt: "L'interdiction de louer les passoires thermiques s'accélère. DPE F et G, calendrier 2028-2034, obligations propriétaires, sanctions.",
    author: "Équipe Batisur",
    date: "2025-11-25",
    category: "Réglementation",
    tags: ["DPE", "loi climat", "bailleur", "passoire thermique", "location"],
    keywords: "loi climat DPE 2026 obligation, DPE F G interdiction location, passoire thermique interdite, rénovation énergétique bailleur, loi Climat Résilience 2028 2034, sanction DPE non conforme",
    readTime: 8,
    heroSubtitle: "Le calendrier complet + comment se préparer",
    body: `
## Le calendrier des interdictions

- **2023** : Interdiction d'augmenter les loyers des DPE F et G (déjà en vigueur)
- **1er janvier 2025** : Interdiction de louer les **DPE G** (déjà en vigueur)
- **1er janvier 2028** : Interdiction de louer les **DPE F**
- **1er janvier 2034** : Interdiction de louer les **DPE E**

## Qui est concerné ?

- Tout propriétaire bailleur d'un logement classé F ou G (~4.8 millions de logements en France)
- Tout nouveau bail signé pour un logement F ou G (même si le locataire actuel reste)
- Ventes : DPE + audit énergétique OBLIGATOIRE pour F et G (info acheteur)

## Sanctions

Un locataire peut :
- **Refuser de payer le loyer** si le logement passoire est loué (dommages-intérêts)
- Saisir la Commission Départementale de Conciliation
- Saisir le tribunal pour **résiliation du bail** aux torts du bailleur
- Demander l'**exécution forcée** des travaux (sous astreinte)

## Comment savoir si mon logement est concerné ?

DPE réalisé après le 1er juillet 2021 (nouveau DPE opposable) :
- **A** : très économe (< 70 kWh/m²/an)
- **B** : économe (71-110)
- **C** : équilibré (111-180)
- **D** : correct (181-250)
- **E** : énergivore (251-330) — **interdit à la location en 2034**
- **F** : passoire thermique (331-420) — **interdit en 2028**
- **G** : très mauvais (> 420) — **déjà interdit depuis 2025**

## Combien coûte le passage F → C ?

Pour une maison 100m² classée F, le bouquet type est :
- Isolation combles (25€/m² × 100 = 2 500€)
- ITE façade (150€/m² × 80m² = 12 000€)
- Remplacement chaudière fioul par PAC air/eau (12 000€)
- VMC double flux (4 500€)
- Fenêtres double vitrage (10 000€)

**Total : 41 000€ (avant aides)**

Avec MaPrimeRénov' Sérénité + CEE + éco-PTZ : **reste à charge 12-20 000€** selon revenus.

## Stratégies pour les propriétaires bailleurs

### 1. Anticiper (rénovation entre locataires)
Profitez d'un départ de locataire pour lancer les travaux (logement vide 2-6 mois selon ampleur).

### 2. Bouquet 2 gestes minimum
Sortez du F sans forcément aller jusqu'au A. Passage F → E est déjà autorisé jusqu'en 2034.

### 3. Vendre avant l'échéance
Si les travaux sont trop lourds : vendre en 2026-2027 avant que l'obligation ne fasse chuter les prix des passoires thermiques.

### 4. Investir dans du neuf
Un logement neuf RE 2020 est classé A ou B — aucune contrainte future à prévoir.

## Sécuriser 40 000€ de travaux énergétiques avec Batisur

Sur Batisur, votre chantier rénovation énergétique est :
- **Confié à des artisans RGE** (obligatoire pour aides)
- **Payé en séquestre échelonné** par étape validée
- **Documenté par photos horodatées** (preuve pour MaPrimeRénov' Sérénité)
- **Certifié** par un PDF de fin de travaux opposable

**Bonus** : les artisans Batisur émettent une facture Factur-X 2026 conforme, obligatoire pour toucher les aides.

[Publier ma mission rénovation énergétique →](/travaux/renovation-energetique)
    `,
  },

  "guide-architecte-choisir-sous-traitants-2026": {
    slug: "guide-architecte-choisir-sous-traitants-2026",
    title: "Guide architecte 2026 : comment choisir et sécuriser vos sous-traitants BTP",
    excerpt: "Vous êtes architecte DPLG ou HMONP et vous coordonnez plusieurs artisans sur un même chantier ? Voici la méthode complète pour sélectionner, contractualiser et sécuriser financièrement vos sous-traitants en 2026.",
    author: "Équipe Batisur",
    date: "2026-08-05",
    category: "B2B · Architectes",
    tags: ["architecte", "sous-traitance", "coordination", "MOE", "chantier"],
    keywords: "architecte choisir sous-traitants, architecte gérer artisans, MOE coordination BTP, cabinet architecture outil chantier, architecte DPLG plateforme, appel de fonds architecte, architecte responsabilité sous-traitant",
    readTime: 11,
    heroSubtitle: "La méthode complète pour piloter vos artisans sans risque financier",
    body: `
## Pourquoi la coordination architecte est un métier à risque

En tant qu'architecte, vous engagez **votre responsabilité civile professionnelle** dès la maîtrise d'œuvre (MOE) débute. Un sous-traitant qui disparaît avec l'acompte, une malfaçon non détectée, une facture Factur-X 2026 non conforme… et c'est vous que le client attaque. La réforme obligatoire de la facturation électronique B2B en septembre 2026 rajoute une couche de complexité administrative.

## Les 5 critères non négociables pour sélectionner un sous-traitant

### 1. SIRET vérifié en temps réel via INSEE Sirene
Un SIRET peut être radié entre le devis et la première facture. La vérification doit être **automatique et continue**. Sur Batisur Connect, l'API Sirene est interrogée à chaque connexion artisan.

### 2. Assurance décennale valide et couvrant votre lot
Demandez systématiquement l'attestation en cours de validité + précisez que le lot spécifique (VRD, gros œuvre, plomberie…) est couvert. Un artisan couvert "peinture" ne l'est pas pour la maçonnerie.

### 3. Historique de chantiers documenté par photos
Les portfolios PDF sont souvent embellis. Exigez des photos horodatées GPS de chantiers récents. Le journal de chantier Batisur remplit exactement cette fonction : chaque étape historique est opposable.

### 4. Capacité financière proportionnelle au lot
Un artisan à 3 salariés ne peut pas absorber un lot à 400k€. Vérifiez le CA sur infogreffe.fr. Batisur intègre un badge "Or/Premium" indexé sur la solidité financière.

### 5. Avis clients vérifiés (pas Google Reviews)
Les avis Google sont facilement falsifiés. Sur Batisur, seules les personnes ayant réellement payé un chantier peuvent laisser un avis — impossible à truquer.

## Le contrat sous-traitance loi 1975 : ce qui est obligatoire en 2026

Depuis la loi n°75-1334 (mise à jour 2026), le contrat de sous-traitance doit contenir :
- Identification exacte du sous-traitant (SIRET + adresse)
- Objet précis du lot avec quantitatif détaillé
- Prix ferme ou révisable (formule d'indexation)
- **Modalités de paiement échelonné** — c'est là que Batisur excelle
- Délais de livraison par phase
- Pénalités de retard chiffrées
- Assurance décennale du sous-traitant

## L'appel de fonds échelonné : la clé de la sécurité architecte

Au lieu de payer 30% + 40% + 30%, structurez en **5 à 8 tranches** liées à des jalons photos :
1. Démarrage (5%) — sur bon de commande
2. Fondations validées (15%) — photos horodatées GPS
3. Élévations 1er niveau (15%) — journal chantier
4. Hors d'eau (20%) — photos toiture
5. Hors d'air (15%) — photos menuiseries
6. Cloisons/électricité (15%) — photos gaines
7. Finitions (10%) — photos finales
8. Réception & PV (5%) — signature client

Sur Batisur, chaque tranche est **séquestrée sur Stripe** et libérée automatiquement dès que la photo horodatée est publiée + votre validation architecte.

## Facturation Factur-X 2026 : votre obligation légale à partir de septembre 2026

À compter du **1er septembre 2026**, toute facture B2B en France doit être émise au format **Factur-X (PDF/A-3 + XML EN 16931)**. Les artisans qui envoient encore des factures Excel ou Word ne pourront plus être payés légalement. Batisur génère automatiquement toutes les factures artisans + votre facturation MOE au format conforme.

## Cas concret : Cabinet Bertholet & Fils (Lyon)

Cabinet Bertholet supervise 8 chantiers/an à budget moyen 350k€. Avant Batisur : 40h/mois de gestion administrative, 2 impayés en 2024 (18k€ perdus). Depuis Batisur (janvier 2026) : **0 impayé, 40h récupérées, 3 nouveaux clients acquis grâce à l'argument "chantier sécurisé"**.

## Prochaines étapes pour votre cabinet

1. [Créer votre compte architecte sur Batisur](/register?role=demandeur&persona=architecte) — gratuit, sans engagement
2. Inviter votre premier sous-traitant de confiance
3. Sécuriser votre prochain chantier en séquestre Stripe

[Ouvrir mon compte architecte Batisur →](/register?role=demandeur&persona=architecte)
    `,
  },

  "syndic-securiser-500k-travaux-copropriete": {
    slug: "syndic-securiser-500k-travaux-copropriete",
    title: "Syndic 2026 : comment sécuriser 500 000€ de travaux votés en AG (sans contestation)",
    excerpt: "Ravalement de façade, ravalement toiture, ITE, ascenseur… les gros travaux votés en assemblée générale sont votre principal risque de contentieux syndic. Voici comment traiter 500k€ de travaux copropriété sans qu'un seul copropriétaire ne conteste.",
    author: "Équipe Batisur",
    date: "2026-08-06",
    category: "B2B · Syndics",
    tags: ["syndic", "copropriété", "AG", "travaux", "ravalement"],
    keywords: "syndic sécuriser travaux copropriété, ravalement copropriété 2026, syndic AG travaux gros montant, syndic contentieux copropriétaire, gros travaux copropriété paiement, syndic vote AG travaux, syndic outil gestion travaux",
    readTime: 10,
    heroSubtitle: "La méthode syndics pour zéro contestation copropriétaire",
    body: `
## Le problème n°1 des syndics en 2026 : la contestation post-AG

Selon la FNAIM, **65% des syndics font face à au moins une contestation par an** sur des travaux votés en AG. Le grief récurrent des copropriétaires : "on ne sait pas où passe l'argent". Ravalement à 380k€, ITE à 250k€, réfection toiture à 180k€… les sommes sont énormes et la traçabilité est vécue comme opaque.

## Ce que la loi 2026 exige d'un syndic sur les gros travaux

- **Traçabilité complète** de chaque euro dépensé (loi ELAN + décret 2020)
- **Appels de fonds détaillés** par phase de travaux (article 14-1 loi 1965)
- **PV de réception** signé par MOE et représentant du conseil syndical
- **Facturation Factur-X 2026** obligatoire à compter de septembre 2026

Un syndic qui ne peut pas justifier ligne par ligne les 500k€ dépensés s'expose à une **révocation en AG et poursuites civiles**.

## La méthode Batisur pour syndics : traçabilité opposable

### Étape 1 : Vote AG et budgétisation
Après vote AG, vous saisissez le chantier sur Batisur avec le budget total voté. Les fonds appelés aux copropriétaires sont versés sur le compte séquestre Stripe — **les copropriétaires voient en temps réel que l'argent est là, bloqué, et non transféré à l'entreprise**.

### Étape 2 : Sélection artisan avec SIRET vérifié
Les entreprises BTP invitées pour le chantier sont vérifiées SIRET, assurance décennale, capacité financière. Vous pouvez inviter directement une entreprise déjà connue du syndic.

### Étape 3 : Découpage en phases claires
Pour un ravalement 380k€ par exemple :
- Nacelle/échafaudage (15%) — photo installation
- Grattage/décapage (10%) — photos avant/après
- Ragréage/enduits (30%) — photos par façade
- Peinture (30%) — photos par façade
- Rebouchage/finitions (10%) — photos détaillées
- Réception (5%) — PV signé

### Étape 4 : Libération conditionnée aux preuves
Chaque phase = **photos horodatées GPS + timestamp SHA-256 non falsifiable**. Ces preuves sont :
- Consultables par tout copropriétaire connecté à son espace
- Joignables au PV d'AG N+1
- Opposables devant un juge en cas de contentieux

### Étape 5 : Facturation Factur-X 2026 par phase
Chaque phase déclenche une facture Factur-X conforme. Le comptable du syndic récupère les XML automatiquement pour son intégration comptable.

## Cas concret : Foncia Paris 17e (ravalement 350k€)

Le syndic Foncia gère un immeuble haussmannien 42 lots. Ravalement voté en AG mai 2025 pour 350k€. Sécurisation Batisur mise en place. **Résultat AG 2026 : zéro contestation, zéro question sur les dépenses, 100% des copropriétaires votent le quitus du syndic à l'unanimité.** Les photos horodatées jointes au PV d'AG ont "coupé court à toutes les rumeurs" selon le syndic.

## Batisur vs les outils classiques syndic

| Fonction | Outil syndic classique | Batisur Connect |
|---|---|---|
| Vérification SIRET | Manuelle, souvent oubliée | Automatique via INSEE |
| Fonds copropriétaires | Compte syndic (mélangé) | Séquestre Stripe dédié |
| Preuves d'avancement | Photos jointes email (falsifiables) | Photos horodatées GPS + SHA-256 |
| Facturation 2026 | À vérifier au cas par cas | Factur-X natif |
| Contestation copropriétaire | Fréquente | Impossible (preuves opposables) |

## Prochaines étapes pour votre cabinet syndic

1. [Créer votre compte syndic Batisur](/register?role=demandeur&persona=syndic)
2. Sécuriser votre prochain gros chantier voté en AG
3. Présenter Batisur en AG comme argument de professionnalisme

[Ouvrir mon compte syndic Batisur →](/register?role=demandeur&persona=syndic)
    `,
  },

  "promoteur-reduire-impayes-sous-traitants-80": {
    slug: "promoteur-reduire-impayes-sous-traitants-80",
    title: "Promoteur immobilier : comment réduire de 80% les impayés et retards sous-traitants",
    excerpt: "Un programme immobilier neuf à 8M€ mobilise 40+ sous-traitants sur 24 mois. Chaque retard de paiement crée un retard de livraison. Voici la méthode 2026 pour tenir les délais et livrer sans stress.",
    author: "Équipe Batisur",
    date: "2026-08-07",
    category: "B2B · Promoteurs",
    tags: ["promoteur", "immobilier", "sous-traitance", "SCCV", "VEFA"],
    keywords: "promoteur immobilier sécuriser sous-traitants, réduire impayés sous-traitance BTP, promoteur SCCV outil, promoteur immobilier logiciel chantier, VEFA sécurisation, promoteur retard livraison, promoteur paiement sous-traitants",
    readTime: 10,
    heroSubtitle: "La méthode 2026 des promoteurs qui livrent dans les délais",
    body: `
## Le paradoxe du promoteur : trop de sous-traitants, pas assez de contrôle

Un programme neuf de 32 lots (SCCV budget 4M€) mobilise en moyenne **40 sous-traitants sur 24 mois**. Gros œuvre, VRD, plomberie, électricité, cloisons, revêtements, ascenseurs, menuiseries, façades, espaces verts… Chaque défaillance de trésorerie chez un sous-traitant crée un effet domino qui bloque le chantier suivant.

Selon la FPI (Fédération des Promoteurs Immobiliers), **le retard moyen de livraison est de 4,2 mois en 2025**, et 60% des retards sont liés à des problèmes de paiement sous-traitants (ou d'abandon de chantier).

## Pourquoi les promoteurs adoptent le séquestre Stripe en 2026

### Motif 1 : rassurer les acquéreurs VEFA
En VEFA, l'acquéreur libère 5%, 25%, 40%, 65%, 95%, 100% selon jalons. **Le promoteur doit prouver l'avancement**. Les photos horodatées géolocalisées Batisur remplacent avantageusement le rapport huissier (coût divisé par 10).

### Motif 2 : sécuriser les sous-traitants
Un sous-traitant qui sait que ses fonds sont **déjà en séquestre chez Stripe** attaque son chantier avec 3x plus d'engagement. Il sait qu'il sera payé à réception validée. Cela accélère massivement les délais.

### Motif 3 : reporting actionnaires temps réel
Les SCCV avec plusieurs co-investisseurs ont besoin de rapports mensuels. Batisur génère automatiquement le tableau de bord programme : % avancement par lot, budget consommé, prévisionnel de trésorerie.

## La méthode 3T : Tranche, Traçabilité, Trésorerie

### Tranche : découpage par macro-lot
Chaque macro-lot (VRD, gros œuvre, second œuvre, finitions) devient un **chantier Batisur indépendant** avec son budget séquestré et sa chronologie propre.

### Traçabilité : preuves opposables
Chaque sous-traitant documente ses jalons par photos horodatées SHA-256. À réception, vous exportez un rapport PDF opposable pour :
- Les acquéreurs VEFA
- Les assureurs décennale
- Le contrôleur technique (si Sécuritas, Apave, Bureau Veritas)
- La banque prêteuse (si mise en jeu de la garantie financière d'achèvement)

### Trésorerie : jamais un centime avancé
Le principe Batisur : **0 acompte non couvert par photo**. Vous payez uniquement ce qui est physiquement construit et documenté. Cela protège votre trésorerie et vous évite les dérives budget.

## Cas concret : SCCV Riviera Green (programme 4M€, Nice)

Programme de 32 lots livrable T2 2027. Batisur déployé dès la phase gros œuvre (novembre 2025). **Résultats 8 mois plus tard** :
- 0 retard de paiement sous-traitant
- 0 contentieux (vs 3 en moyenne sur les précédents programmes)
- Livraison prévue **avec 3 semaines d'avance**
- 100% des acquéreurs VEFA satisfaits du reporting mensuel
- Coût de reporting divisé par 4 (vs bureau de contrôle externalisé)

## Conformité Factur-X 2026 pour votre chaîne comptable

À partir du **1er septembre 2026**, toutes les factures sous-traitants doivent être au format Factur-X (PDF/A-3 + XML EN 16931). Sans cela, le paiement est **juridiquement bloqué**. Batisur émet toutes les factures au format conforme, injectables directement dans votre ERP comptable (SAP, Cegid, Sage).

## Prochaines étapes pour votre société de promotion

1. [Contacter l'équipe promoteurs Batisur](/register?role=demandeur&persona=promoteur)
2. Test sur un macro-lot d'un chantier en cours
3. Déploiement complet sur votre prochain programme

[Contacter l'équipe promoteurs →](/register?role=demandeur&persona=promoteur)
    `,
  },

  "bailleur-social-preuve-photo-renovation": {
    slug: "bailleur-social-preuve-photo-renovation",
    title: "Bailleur social 2026 : la preuve photo devient standard interne pour vos rénovations",
    excerpt: "OPH, ESH, foncière privée : vous rénovez 100-500 logements par an. Comment prouver chaque euro dépensé à vos financeurs (ANRU, Action Logement, banques CDC) sans y passer 20h/logement ? La réponse en 2026.",
    author: "Équipe Batisur",
    date: "2026-08-08",
    category: "B2B · Bailleurs",
    tags: ["bailleur social", "OPH", "ESH", "rénovation énergétique", "ANRU"],
    keywords: "bailleur social outil rénovation, OPH ESH plateforme travaux, ANRU justification travaux, bailleur social preuve photo, foncière rénovation énergétique, bailleur CDC financement, bailleur rénovation logements",
    readTime: 9,
    heroSubtitle: "La méthode standard pour justifier vos travaux ANRU/Action Logement",
    body: `
## Le fardeau administratif des bailleurs sociaux en 2026

Un OPH (office public de l'habitat) ou une ESH (entreprise sociale pour l'habitat) rénove en moyenne **180 à 500 logements par an**. Chaque logement doit être **justifié à ses financeurs** : ANRU (Agence Nationale pour la Rénovation Urbaine), Action Logement, Caisse des Dépôts, éventuellement la Banque des Territoires.

Chaque contrôle exige :
- Factures conformes (Factur-X 2026 à partir de septembre)
- Preuves photo avant/pendant/après
- Attestations d'entreprises RGE (si aide énergétique)
- PV de réception signés par le locataire
- Certificat de fin de travaux

En moyenne, un bailleur estime à **18-25h par logement** le temps administratif de justification. Sur 300 logements/an, c'est **7500 heures de travail administratif**.

## Comment Batisur divise ce temps par 5

### Un journal chantier par logement
Chaque logement rénové = un chantier Batisur indépendant. L'artisan documente sa progression par photos horodatées géolocalisées. À la fin : **export PDF opposable** en 1 clic.

### Factures Factur-X automatiques
Toutes les factures artisans sont émises au format Factur-X 2026 conforme, avec :
- TVA 5,5% pour travaux énergétiques (isolation, chaudière PAC, DPE)
- TVA 10% pour gros entretien
- TVA 20% pour reconstructions

Le service comptable du bailleur récupère les XML automatiquement pour reddition ANRU.

### Traçabilité RGE pour aides énergétiques
Les artisans Batisur affichent leur qualification RGE (Reconnu Garant de l'Environnement) — indispensable pour toucher MaPrimeRénov' Sérénité, CEE, éco-PTZ. La preuve RGE est stockée avec chaque devis.

### Reporting DPE post-travaux
Batisur intègre l'export CSV de vos rénovations avec DPE avant/après — critique pour la loi Climat & Résilience et la sortie des passoires thermiques d'ici 2028 (F/G).

## Cas concret : Foncière Habitat Sud (Marseille, 120 logements/an)

Foncière Habitat Sud a déployé Batisur sur l'ensemble de son parc en janvier 2026. **Résultats à 6 mois** :
- **-70% de temps administratif** par logement (de 20h à 6h)
- Justificatifs ANRU acceptés du premier coup à 100% (vs 40% avant)
- 0 litige avec les locataires (photos horodatées visibles côté locataire)
- Économie estimée : **210 000€/an** de charges administratives

## Conformité 2028 : passoires thermiques et loi Climat

À partir du **1er janvier 2028**, les logements classés F et G sont **interdits à la location** (loi Climat & Résilience). Cela oblige les bailleurs à massifier les rénovations énergétiques dès 2026-2027. Batisur fournit le cadre standardisé pour industrialiser ces rénovations sans exploser vos équipes internes.

## Prochaines étapes pour votre organisme

1. [Créer un compte bailleur Batisur](/register?role=demandeur&persona=bailleur)
2. Démonstration sur 5 logements pilotes
3. Déploiement complet sur votre parc

[Ouvrir mon compte bailleur →](/register?role=demandeur&persona=bailleur)
    `,
  },

  "entreprise-btp-sous-traitance-loi-1975": {
    slug: "entreprise-btp-sous-traitance-loi-1975",
    title: "Entreprise générale BTP : sous-traitance loi 1975 en 2026, mode d'emploi complet",
    excerpt: "Vous êtes entreprise générale gros œuvre ou second œuvre et vous jonglez avec 8-15 sous-traitants par chantier ? Voici comment respecter la loi 1975 tout en industrialisant votre gestion de sous-traitance.",
    author: "Équipe Batisur",
    date: "2026-08-09",
    category: "B2B · Entreprises BTP",
    tags: ["entreprise BTP", "sous-traitance", "loi 1975", "gros œuvre"],
    keywords: "entreprise BTP sous-traitance loi 1975, gestion sous-traitants BTP, entreprise générale gros œuvre outil, second œuvre sous-traitance, avenant sous-traitant BTP, facture Factur-X entreprise BTP, marge entreprise BTP",
    readTime: 10,
    heroSubtitle: "Industrialiser votre sous-traitance sans risquer une requalification",
    body: `
## Loi 1975 : ce qui a changé en 2026 pour les entreprises BTP

La loi n°75-1334 du 31 décembre 1975 sur la sous-traitance a été **renforcée par le décret 2024-2126 applicable en 2026**. Trois évolutions majeures pour les entreprises générales BTP :

1. **Vérification SIRET renforcée** : le donneur d'ordre est solidairement responsable si le sous-traitant est radié
2. **Paiement direct obligatoire** au sous-traitant si non payé sous 45 jours par l'entreprise principale
3. **Facturation Factur-X 2026** dès septembre 2026 pour toute la chaîne de sous-traitance

Le non-respect expose l'entreprise à :
- **Amende de 75 000€** minimum par manquement
- Requalification du contrat en travail dissimulé (pénal)
- Solidarité financière avec le sous-traitant défaillant

## La méthode Batisur pour respecter loi 1975 sans surcharge

### 1. Onboarding sous-traitant automatique
Chaque sous-traitant que vous invitez sur Batisur passe par :
- Vérification SIRET INSEE en temps réel
- Upload attestation décennale à jour
- Attestation URSSAF < 6 mois
- Kbis à jour

L'onboarding prend **8 minutes en moyenne** vs 2h en gestion papier.

### 2. Contrat de sous-traitance électronique
Batisur génère automatiquement le contrat conforme loi 1975 avec :
- Identification exacte des parties
- Objet du lot avec quantitatif
- Prix ferme ou révisable
- Modalités de paiement échelonné
- Délais et pénalités
- Signature électronique légale (eIDAS)

### 3. Multi-lots dans un chantier unique
Un chantier BTP = plusieurs lots (VRD, GO, plomberie, élec, cloisons, peinture, revêtements…). Chaque lot est **un sous-traitant distinct avec son propre budget séquestré**. Vous voyez en temps réel l'avancement de chaque lot.

### 4. Avenants signés numériquement
Un ajustement de budget (avenant) est fréquent sur un chantier BTP. Batisur permet la **signature électronique de l'avenant en 3 clics** avec traçabilité intégrale.

### 5. Facturation Factur-X en cascade
Chaque sous-traitant émet sa facture Factur-X. Vous consolidez et émettez votre propre facture Factur-X au maître d'ouvrage. **Toute la chaîne est conforme réforme 2026**.

## Le gain marge nette : +12% en moyenne

Nos entreprises BTP clientes témoignent d'un **gain de marge nette de +12% en moyenne** sur les chantiers gérés via Batisur. Les sources de ce gain :
- 0 impayé (économie moyenne 15k€/chantier)
- 0 retard de paiement client (délai divisé par 3)
- 0 contentieux sous-traitance (économie juridique 8-20k€/an)
- 40% de temps administratif économisé
- Argument commercial fort (justifie 5-8% de marge additionnelle)

## Cas concret : GEB Construction (14 sous-traitants gérés sur 1 chantier)

GEB Construction est une entreprise générale du Var (23 salariés). Ils ont géré un chantier hôtel 4* à 2,8M€ avec **14 sous-traitants simultanés** via Batisur. **Résultats à réception** :
- 0 retard de livraison (vs moyenne 6 semaines sur chantiers similaires)
- Marge nette réelle **13,4%** (vs 11,8% budget)
- Aucun litige sous-traitance
- Réception maître d'ouvrage sans réserve

## Prochaines étapes pour votre entreprise

1. [Créer votre compte entreprise BTP](/register?role=demandeur&persona=entreprise_btp)
2. Migration progressive : commencez par 1 chantier pilote
3. Déploiement sur toute votre activité

[Ouvrir mon compte entreprise BTP →](/register?role=demandeur&persona=entreprise_btp)
    `,
  },
};

export const BLOG_POSTS_LIST = Object.values(BLOG_POSTS).sort((a, b) => b.date.localeCompare(a.date));
